import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import FotoChecklist from '../components/FotoChecklist';

// Comprime a foto no navegador antes de subir (evita estourar limite e economiza dados no celular)
const comprimirImagem = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const max = 1280;
      let { width, height } = img;
      if (width > max || height > max) {
        if (width > height) { height = Math.round(height * max / width); width = max; }
        else { width = Math.round(width * max / height); height = max; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.72));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
const STATUS_INFO = {
  pendente:  { label: 'Pendente',  cor: '#8a6d00', bg: '#fff8e1', icon: 'bi-hourglass-split' },
  atrasada:  { label: 'Atrasada',  cor: '#b23b3b', bg: '#fdeeee', icon: 'bi-exclamation-triangle-fill' },
  concluida: { label: 'Concluída', cor: '#1f7a3d', bg: '#e9f6ee', icon: 'bi-check-circle-fill' },
  nao_feita: { label: 'Não feita', cor: '#6b6b6b', bg: '#eeeeee', icon: 'bi-x-circle-fill' },
};

export default function Checklist() {
  const { temPermissao } = useAuth();
  const podeGerenciar = temPermissao('checklist_gerenciar');
  return (
    <div>
      <h4 className="fw-bold mb-4"><i className="bi bi-check2-square me-2"></i>Checklist de Campanha</h4>
      {podeGerenciar ? <VisaoGestor /> : <VisaoGerente />}
    </div>
  );
}

// =========================================================================
// GESTOR — cria/gerencia tarefas e acompanha o progresso de cada loja
// =========================================================================
function VisaoGestor() {
  const [campanhas, setCampanhas] = useState([]);
  const [searchParams] = useSearchParams();
  // ?campanha=X: vem de uma demanda gerada (Demandas → ação "Checklist das lojas")
  const [campanhaId, setCampanhaId] = useState(searchParams.get('campanha') || '');
  const [dados, setDados] = useState(null); // { filiais_participantes, tarefas }
  const [filiaisMap, setFiliaisMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandida, setExpandida] = useState(null);

  // Rascunho de novas tarefas (criação em lote)
  const [novas, setNovas] = useState([]);
  const [form, setForm] = useState({ titulo: '', descricao: '', prazo: '', escopo: 'todas', filiais: [] });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/campanhas`).then(r => setCampanhas(r.data)).catch(() => {});
    axios.get(`${API}/filiais`).then(r => { const m = {}; r.data.forEach(f => { m[parseInt(f.codigo)] = f.nome; }); setFiliaisMap(m); }).catch(() => {});
  }, []);

  const carregar = useCallback(() => {
    if (!campanhaId) { setDados(null); return; }
    setLoading(true);
    axios.get(`${API}/checklist/campanha/${campanhaId}/tarefas`).then(r => setDados(r.data)).catch(() => setDados({ filiais_participantes: [], tarefas: [] })).finally(() => setLoading(false));
  }, [campanhaId]);
  useEffect(() => { carregar(); }, [carregar]);

  const nomeFilial = (c) => filiaisMap[c] || `Filial ${c}`;
  const participantes = dados?.filiais_participantes || [];

  const addRascunho = () => {
    if (!form.titulo.trim()) return;
    if (form.escopo === 'especificas' && !form.filiais.length) return Swal.fire('Atenção', 'Escolha ao menos uma loja', 'warning');
    setNovas(prev => [...prev, { ...form, titulo: form.titulo.trim() }]);
    setForm({ titulo: '', descricao: '', prazo: '', escopo: 'todas', filiais: [] });
  };
  const salvarNovas = async () => {
    if (!novas.length) return;
    setSalvando(true);
    try {
      await axios.post(`${API}/checklist/campanha/${campanhaId}/tarefas`, { tarefas: novas });
      setNovas([]); carregar();
      Swal.fire({ icon: 'success', title: 'Tarefas criadas!', timer: 1400, showConfirmButton: false });
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Erro ao criar', 'error'); }
    finally { setSalvando(false); }
  };

  const editarTarefa = async (t) => {
    const chkFiliais = participantes.map(c => `<label style="display:inline-flex;align-items:center;gap:4px;margin:2px 8px 2px 0;font-size:.85rem"><input type="checkbox" class="ed-fil" value="${c}" ${t.filiais.includes(c) ? 'checked' : ''}> ${nomeFilial(c)}</label>`).join('');
    const { value } = await Swal.fire({
      title: 'Editar tarefa',
      html: `
        <input id="ed-tit" class="swal2-input" placeholder="Título" value="${(t.titulo || '').replace(/"/g, '&quot;')}">
        <textarea id="ed-desc" class="swal2-textarea" placeholder="Descrição (opcional)">${t.descricao || ''}</textarea>
        <label style="display:block;text-align:left;font-size:.8rem;margin:6px 10px 0">Prazo:</label>
        <input id="ed-prazo" type="date" class="swal2-input" value="${t.prazo ? String(t.prazo).slice(0, 10) : ''}">
        <div style="text-align:left;padding:0 10px;margin-top:6px">
          <label style="font-size:.85rem"><input type="radio" name="ed-esc" value="todas" ${t.escopo !== 'especificas' ? 'checked' : ''}> Todas as lojas</label><br>
          <label style="font-size:.85rem"><input type="radio" name="ed-esc" value="especificas" ${t.escopo === 'especificas' ? 'checked' : ''}> Lojas específicas</label>
          <div id="ed-fil-box" style="margin-top:4px">${chkFiliais}</div>
        </div>`,
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: '#5C3D52',
      preConfirm: () => {
        const escopo = document.querySelector('input[name="ed-esc"]:checked')?.value || 'todas';
        return {
          titulo: document.getElementById('ed-tit').value,
          descricao: document.getElementById('ed-desc').value,
          prazo: document.getElementById('ed-prazo').value || null,
          escopo,
          filiais: escopo === 'especificas' ? [...document.querySelectorAll('.ed-fil:checked')].map(c => parseInt(c.value)) : [],
        };
      },
    });
    if (!value || !value.titulo.trim()) return;
    await axios.put(`${API}/checklist/tarefas/${t.id}`, value);
    carregar();
  };

  const excluirTarefa = async (t) => {
    const r = await Swal.fire({ title: 'Excluir tarefa?', text: t.titulo, icon: 'warning', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#b23b3b' });
    if (!r.isConfirmed) return;
    await axios.delete(`${API}/checklist/tarefas/${t.id}`);
    carregar();
  };

  const reabrir = async (tarefaId, filial) => {
    const { value, isConfirmed } = await Swal.fire({ title: `Reabrir p/ ${nomeFilial(filial)}?`, text: 'Volta pra pendente pra loja refazer.', input: 'text', inputPlaceholder: 'Comentário (o que refazer)', showCancelButton: true, confirmButtonText: 'Reabrir', cancelButtonText: 'Cancelar', confirmButtonColor: '#5C3D52' });
    if (!isConfirmed) return;
    await axios.post(`${API}/checklist/tarefas/${tarefaId}/filial/${filial}/reabrir`, { comentario: value || '' });
    carregar();
  };

  // Apaga por completo a resposta de uma loja (status + obs + justificativa + foto)
  const excluirResposta = async (tarefaId, filial) => {
    const r = await Swal.fire({ title: `Apagar resposta de ${nomeFilial(filial)}?`, text: 'Remove status, observação, justificativa e foto dessa loja — volta ao branco.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Apagar', cancelButtonText: 'Cancelar', confirmButtonColor: '#b23b3b' });
    if (!r.isConfirmed) return;
    await axios.delete(`${API}/checklist/tarefas/${tarefaId}/filial/${filial}/status`);
    carregar();
  };

  const toggleFilForm = (c) => setForm(f => ({ ...f, filiais: f.filiais.includes(c) ? f.filiais.filter(x => x !== c) : [...f.filiais, c] }));

  return (
    <div>
      <div className="card p-3 mb-3">
        <label className="form-label" style={{ fontSize: '0.85rem' }}>Campanha</label>
        <select className="form-select" style={{ maxWidth: 480 }} value={campanhaId} onChange={e => { setCampanhaId(e.target.value); setNovas([]); setExpandida(null); }}>
          <option value="">Selecione a campanha...</option>
          {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>

      {campanhaId && (
        <>
          {/* Criação em lote */}
          <div className="card p-3 mb-3">
            <h6 className="fw-bold mb-2"><i className="bi bi-plus-circle me-1"></i>Nova tarefa</h6>
            <div className="row g-2">
              <div className="col-md-5"><input className="form-control" placeholder="Título (ex: Limpar os pisos do mostruário)" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} onKeyDown={e => e.key === 'Enter' && addRascunho()} /></div>
              <div className="col-md-4"><input className="form-control" placeholder="Descrição (opcional)" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
              <div className="col-md-3"><input type="date" className="form-control" value={form.prazo} onChange={e => setForm({ ...form, prazo: e.target.value })} title="Prazo" /></div>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap mt-2">
              <label style={{ fontSize: '0.85rem' }}><input type="radio" checked={form.escopo === 'todas'} onChange={() => setForm({ ...form, escopo: 'todas', filiais: [] })} /> Todas as lojas</label>
              <label style={{ fontSize: '0.85rem' }}><input type="radio" checked={form.escopo === 'especificas'} onChange={() => setForm({ ...form, escopo: 'especificas' })} /> Lojas específicas</label>
              {form.escopo === 'especificas' && (
                <div className="d-flex flex-wrap gap-1">
                  {participantes.map(c => (
                    <button key={c} type="button" onClick={() => toggleFilForm(c)} className="btn btn-sm" style={{ fontSize: '0.78rem', border: `1px solid ${form.filiais.includes(c) ? '#6B2C5C' : '#ddd'}`, background: form.filiais.includes(c) ? 'var(--mostarda-claro)' : '#fff', color: form.filiais.includes(c) ? '#6B2C5C' : '#555' }}>
                      {form.filiais.includes(c) && <i className="bi bi-check2 me-1"></i>}{nomeFilial(c)}
                    </button>
                  ))}
                </div>
              )}
              <button className="btn btn-outline-mkt btn-sm ms-auto" onClick={addRascunho} disabled={!form.titulo.trim()}><i className="bi bi-plus-lg me-1"></i>Adicionar à lista</button>
            </div>

            {novas.length > 0 && (
              <div className="mt-3 pt-2" style={{ borderTop: '1px solid #eee' }}>
                {novas.map((n, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '0.85rem' }}>
                    <i className="bi bi-dot"></i>
                    <span className="fw-semibold">{n.titulo}</span>
                    <span className="text-muted">{n.escopo === 'especificas' ? n.filiais.map(nomeFilial).join(', ') : 'Todas'}{n.prazo ? ` · até ${fmtData(n.prazo)}` : ''}</span>
                    <button className="btn btn-sm btn-link text-danger p-0 ms-auto" onClick={() => setNovas(prev => prev.filter((_, x) => x !== i))}><i className="bi bi-x-lg"></i></button>
                  </div>
                ))}
                <button className="btn-salvar mt-2" onClick={salvarNovas} disabled={salvando}>
                  {salvando ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</> : <><i className="bi bi-check-lg me-1"></i>Salvar {novas.length} tarefa{novas.length > 1 ? 's' : ''}</>}
                </button>
              </div>
            )}
          </div>

          {/* Lista + progresso */}
          {loading && <div className="card p-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2" />Carregando...</div>}
          {!loading && dados && dados.tarefas.length === 0 && <div className="card p-4 text-center text-muted">Nenhuma tarefa nesta campanha ainda. Crie acima.</div>}

          {!loading && dados && dados.tarefas.map(t => {
            const aberta = expandida === t.id;
            const pct = t.total_lojas ? Math.round((t.concluidas / t.total_lojas) * 100) : 0;
            return (
              <div key={t.id} className="card p-3 mb-2">
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap" style={{ cursor: 'pointer' }} onClick={() => setExpandida(aberta ? null : t.id)} title="Clique pra ver por loja">
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <strong>{t.titulo}</strong>
                      <span className="badge" style={{ background: '#eee', color: '#555', fontSize: '0.72rem' }}>{t.escopo === 'especificas' ? `${t.total_lojas} loja(s)` : 'Todas as lojas'}</span>
                      {t.prazo && <span className="badge" style={{ background: '#f3eef1', color: '#6B2C5C', fontSize: '0.72rem' }}><i className="bi bi-calendar-event me-1"></i>{fmtData(t.prazo)}</span>}
                    </div>
                    {t.descricao && <div className="text-muted" style={{ fontSize: '0.82rem' }}>{t.descricao}</div>}
                    <div className="d-flex align-items-center gap-2 mt-2" style={{ maxWidth: 420 }}>
                      <div style={{ flex: 1, background: '#eee', borderRadius: 6, height: 8 }}>
                        <div style={{ width: pct + '%', background: '#1f7a3d', height: '100%', borderRadius: 6 }} />
                      </div>
                      <small className="text-muted">{t.concluidas}/{t.total_lojas}</small>
                      {(t.resumo.atrasada > 0) && <small style={{ color: '#b23b3b' }}><i className="bi bi-exclamation-triangle-fill me-1"></i>{t.resumo.atrasada}</small>}
                      {(t.resumo.nao_feita > 0) && <small style={{ color: '#6b6b6b' }}>{t.resumo.nao_feita} não feita</small>}
                    </div>
                  </div>
                  <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => editarTarefa(t)} title="Editar"><i className="bi bi-pencil"></i></button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => excluirTarefa(t)} title="Excluir"><i className="bi bi-trash"></i></button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandida(aberta ? null : t.id)} title="Ver por loja"><i className={`bi bi-chevron-${aberta ? 'up' : 'down'}`}></i></button>
                  </div>
                </div>

                {aberta && (
                  <div className="mt-3 pt-2" style={{ borderTop: '1px solid #eee' }}>
                    <div className="row g-2">
                      {t.por_loja.map(l => {
                        const si = STATUS_INFO[l.status] || STATUS_INFO.pendente;
                        return (
                          <div key={l.filial} className="col-md-6">
                            <div className="p-2 rounded" style={{ border: '1px solid #eee' }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold" style={{ fontSize: '0.85rem' }}><i className="bi bi-shop me-1"></i>{nomeFilial(l.filial)}</span>
                                <span className="badge" style={{ background: si.bg, color: si.cor, fontSize: '0.72rem' }}><i className={`bi ${si.icon} me-1`}></i>{si.label}{l.com_atraso ? ' (atraso)' : ''}</span>
                              </div>
                              {l.observacao && <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}><i className="bi bi-chat-left-text me-1"></i>{l.observacao}</div>}
                              {l.justificativa && <div style={{ fontSize: '0.78rem', color: '#b23b3b' }} className="mt-1"><i className="bi bi-info-circle me-1"></i>{l.justificativa}</div>}
                              {(() => {
                                const temResposta = ['concluida', 'nao_feita'].includes(l.status) || l.tem_foto || l.observacao || l.justificativa;
                                if (!temResposta) return null;
                                return (
                                  <div className="d-flex gap-2 align-items-center mt-1">
                                    {l.tem_foto && <FotoChecklist url={`${API}/checklist/tarefas/${t.id}/filial/${l.filial}/foto`} tamanho={44} />}
                                    <button className="btn btn-sm btn-link p-0 ms-auto" style={{ fontSize: '0.78rem' }} onClick={() => reabrir(t.id, l.filial)}><i className="bi bi-arrow-counterclockwise me-1"></i>reabrir</button>
                                    <button className="btn btn-sm btn-link text-danger p-0" style={{ fontSize: '0.78rem' }} onClick={() => excluirResposta(t.id, l.filial)}><i className="bi bi-trash me-1"></i>apagar</button>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// =========================================================================
// GERENTE DE LOJA — vê e conclui as tarefas da própria loja (mobile-first)
// =========================================================================
function VisaoGerente() {
  const { user } = useAuth();
  const minhaFilial = user?.filial_id;
  const [tarefas, setTarefas] = useState(null);
  const [modalTarefa, setModalTarefa] = useState(null); // tarefa sendo respondida/editada

  const carregar = useCallback(() => {
    axios.get(`${API}/checklist/minhas`).then(r => setTarefas(r.data)).catch(() => setTarefas([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (tarefas === null) return <div className="card p-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2" />Carregando...</div>;
  if (!tarefas.length) return <div className="card p-4 text-center text-muted">Nenhuma tarefa pra sua loja no momento. 🎉</div>;

  const pendentes = tarefas.filter(t => t.status === 'pendente' || t.status === 'atrasada').length;

  // Agrupa por campanha (a lista já vem ordenada por prazo)
  const grupos = [];
  const idx = {};
  tarefas.forEach(t => {
    if (idx[t.campanha_id] === undefined) { idx[t.campanha_id] = grupos.length; grupos.push({ id: t.campanha_id, nome: t.campanha_nome, tarefas: [] }); }
    grupos[idx[t.campanha_id]].tarefas.push(t);
  });

  return (
    <div>
      {pendentes > 0 && <div className="mb-3" style={{ fontSize: '0.9rem', color: '#6B2C5C' }}><b>{pendentes}</b> tarefa(s) pra fazer</div>}

      {grupos.map(g => (
        <div key={g.id} className="card mb-3" style={{ overflow: 'hidden' }}>
          {/* Cabeçalho: a campanha aparece UMA vez */}
          <div style={{ background: 'var(--mostarda)', color: '#fff', padding: '10px 14px' }}>
            <div style={{ fontSize: '0.68rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Campanha</div>
            <div className="fw-bold" style={{ fontSize: '1rem' }}><i className="bi bi-megaphone me-2"></i>{g.nome}</div>
          </div>
          {/* DESKTOP: tabela com Prazo e Status separados */}
          <div className="d-none d-lg-block">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
              <thead style={{ background: '#faf8fb' }}>
                <tr>
                  <th style={{ minWidth: 220 }}>Demanda</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Prazo</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {g.tarefas.map(t => {
                  const si = STATUS_INFO[t.status] || STATUS_INFO.pendente;
                  const feito = t.status === 'concluida' || t.status === 'nao_feita';
                  const venceu = t.status === 'atrasada';
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="fw-semibold">{t.titulo}</div>
                        {t.descricao && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{t.descricao}</div>}
                        {t.observacao && <div className="text-muted mt-1" style={{ fontSize: '0.74rem' }}><i className="bi bi-chat-left-text me-1"></i>{t.observacao}</div>}
                        {t.justificativa && <div style={{ fontSize: '0.74rem', color: '#b23b3b' }} className="mt-1"><i className="bi bi-info-circle me-1"></i>{t.justificativa}</div>}
                        {t.tem_foto && minhaFilial && <div className="mt-1"><FotoChecklist url={`${API}/checklist/tarefas/${t.id}/filial/${minhaFilial}/foto`} tamanho={40} /></div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: venceu ? '#b23b3b' : '#777' }}>
                        {t.prazo ? <><i className="bi bi-calendar-event me-1"></i>{fmtData(t.prazo)}</> : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="badge" style={{ background: si.bg, color: si.cor, fontSize: '0.72rem' }}><i className={`bi ${si.icon} me-1`}></i>{si.label}</span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {feito
                          ? <button className="btn btn-outline-secondary btn-sm" onClick={() => setModalTarefa(t)}><i className="bi bi-pencil me-1"></i>Editar</button>
                          : <button className="btn-salvar" style={{ padding: '5px 14px', fontSize: '0.85rem' }} onClick={() => setModalTarefa(t)}><i className="bi bi-check-lg me-1"></i>Concluir</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* MOBILE: cards empilhados, sem scroll lateral */}
          <div className="d-lg-none">
            {g.tarefas.map(t => {
              const si = STATUS_INFO[t.status] || STATUS_INFO.pendente;
              const feito = t.status === 'concluida' || t.status === 'nao_feita';
              const venceu = t.status === 'atrasada';
              return (
                <div key={t.id} className="p-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ flex: 1 }}>
                      <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{t.titulo}</div>
                      {t.descricao && <div className="text-muted" style={{ fontSize: '0.82rem' }}>{t.descricao}</div>}
                    </div>
                    <span className="badge" style={{ background: si.bg, color: si.cor, fontSize: '0.72rem', whiteSpace: 'nowrap' }}><i className={`bi ${si.icon} me-1`}></i>{si.label}</span>
                  </div>
                  {t.prazo && <div className="mt-1" style={{ fontSize: '0.8rem', color: venceu ? '#b23b3b' : '#777' }}><i className="bi bi-calendar-event me-1"></i>até {fmtData(t.prazo)}</div>}
                  {t.observacao && <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}><i className="bi bi-chat-left-text me-1"></i>{t.observacao}</div>}
                  {t.justificativa && <div style={{ fontSize: '0.78rem', color: '#b23b3b' }} className="mt-1"><i className="bi bi-info-circle me-1"></i>{t.justificativa}</div>}
                  <div className="d-flex align-items-center gap-2 mt-2">
                    {t.tem_foto && minhaFilial && <FotoChecklist url={`${API}/checklist/tarefas/${t.id}/filial/${minhaFilial}/foto`} tamanho={44} />}
                    {feito
                      ? <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={() => setModalTarefa(t)}><i className="bi bi-pencil me-1"></i>Editar</button>
                      : <button className="btn-salvar ms-auto" style={{ padding: '7px 18px' }} onClick={() => setModalTarefa(t)}><i className="bi bi-check-lg me-1"></i>Concluir</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {modalTarefa && (
        <ModalTarefa
          tarefa={modalTarefa}
          minhaFilial={minhaFilial}
          onClose={() => setModalTarefa(null)}
          onSaved={() => { setModalTarefa(null); carregar(); }}
        />
      )}
    </div>
  );
}

// Modal do gerente: conclui/edita com foto + texto opcionais juntos
function ModalTarefa({ tarefa, minhaFilial, onClose, onSaved }) {
  const venceu = tarefa.status === 'atrasada' || tarefa.com_atraso;
  const [status, setStatus] = useState(tarefa.status_real === 'nao_feita' ? 'nao_feita' : 'concluida');
  const [observacao, setObservacao] = useState(tarefa.observacao || '');
  const [justificativa, setJustificativa] = useState(tarefa.justificativa || '');
  const [fotoNova, setFotoNova] = useState(null);           // base64 nova (prévia)
  const [removerFotoFlag, setRemoverFotoFlag] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const pedeJust = status === 'nao_feita' || (status === 'concluida' && venceu);
  const temFotoExistente = tarefa.tem_foto && !removerFotoFlag && !fotoNova;

  const escolherFoto = async (file) => {
    if (!file) return;
    try { setFotoNova(await comprimirImagem(file)); setRemoverFotoFlag(false); }
    catch { setErro('Não consegui ler a foto'); }
  };

  const salvar = async () => {
    if (pedeJust && !justificativa.trim()) { setErro(status === 'nao_feita' ? 'Diga o que deu errado' : 'Diga o motivo do atraso'); return; }
    setSalvando(true); setErro('');
    try {
      await axios.post(`${API}/checklist/tarefas/${tarefa.id}/responder`, { status, observacao: observacao.trim() || null, justificativa: justificativa.trim() || null });
      if (fotoNova) await axios.post(`${API}/checklist/tarefas/${tarefa.id}/foto`, { foto_base64: fotoNova });
      else if (removerFotoFlag && tarefa.tem_foto) await axios.delete(`${API}/checklist/tarefas/${tarefa.id}/foto`);
      onSaved();
    } catch (e) { setErro(e.response?.data?.error || 'Erro ao salvar'); setSalvando(false); }
  };

  const desfazer = async () => {
    setSalvando(true); setErro('');
    try { await axios.post(`${API}/checklist/tarefas/${tarefa.id}/responder`, { status: 'pendente' }); onSaved(); }
    catch (e) { setErro(e.response?.data?.error || 'Erro'); setSalvando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div className="card p-3" style={{ width: '100%', maxWidth: 480, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}><i className="bi bi-megaphone me-1"></i>{tarefa.campanha_nome}</div>
            <h6 className="fw-bold mb-0">{tarefa.titulo}</h6>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        {tarefa.descricao && <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{tarefa.descricao}</div>}
        {tarefa.prazo && <div className="mb-2" style={{ fontSize: '0.82rem', color: venceu ? '#b23b3b' : '#666' }}><i className="bi bi-calendar-event me-1"></i>até {fmtData(tarefa.prazo)}{venceu ? ' · vencida' : ''}</div>}

        <div className="d-flex gap-2 mb-2">
          <button className={`btn btn-sm flex-fill ${status === 'concluida' ? 'btn-salvar' : 'btn-outline-secondary'}`} onClick={() => setStatus('concluida')}><i className="bi bi-check-lg me-1"></i>{venceu ? 'Concluí (atraso)' : 'Concluí'}</button>
          <button className={`btn btn-sm flex-fill ${status === 'nao_feita' ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setStatus('nao_feita')}>Não fiz</button>
        </div>

        {pedeJust && (
          <>
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>{status === 'nao_feita' ? 'O que deu errado? *' : 'Motivo do atraso? *'}</label>
            <textarea className="form-control mb-2" rows="2" value={justificativa} onChange={e => setJustificativa(e.target.value)} placeholder="Obrigatório" />
          </>
        )}

        <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Observação (opcional)</label>
        <textarea className="form-control mb-2" rows="2" value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Ex: feito, tudo certo" />

        <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Foto (opcional)</label>
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          {fotoNova && <><img src={fotoNova} alt="prévia" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} /><button className="btn btn-sm btn-link text-danger p-0" onClick={() => setFotoNova(null)}>remover</button></>}
          {temFotoExistente && minhaFilial && <><FotoChecklist url={`${API}/checklist/tarefas/${tarefa.id}/filial/${minhaFilial}/foto`} tamanho={64} /><button className="btn btn-sm btn-link text-danger p-0" onClick={() => setRemoverFotoFlag(true)}>remover</button></>}
          {!fotoNova && !temFotoExistente && (
            <label className="btn btn-outline-mkt btn-sm mb-0" style={{ cursor: 'pointer' }}>
              <i className="bi bi-camera me-1"></i>Adicionar foto
              <input type="file" accept="image/*" capture="environment" hidden onChange={e => { escolherFoto(e.target.files[0]); e.target.value = ''; }} />
            </label>
          )}
        </div>

        {erro && <div className="text-danger mb-2" style={{ fontSize: '0.8rem' }}><i className="bi bi-exclamation-circle me-1"></i>{erro}</div>}

        <div className="d-flex justify-content-between align-items-center gap-2 mt-1">
          {tarefa.status_real !== 'pendente'
            ? <button className="btn btn-outline-secondary btn-sm" disabled={salvando} onClick={desfazer}><i className="bi bi-arrow-counterclockwise me-1"></i>Desfazer</button>
            : <span />}
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={salvando}>Cancelar</button>
            <button className="btn-salvar" style={{ padding: '6px 16px' }} onClick={salvar} disabled={salvando}>{salvando ? <><span className="spinner-border spinner-border-sm me-1" />Salvando...</> : <><i className="bi bi-check-lg me-1" />Salvar</>}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
